import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded';
import DashboardCustomizeRoundedIcon from '@mui/icons-material/DashboardCustomizeRounded';
import ReportRoundedIcon from '@mui/icons-material/ReportRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { SERVER_ROOT } from 'env';

const ADMIN_PATH = '/admin';
const ADMIN_TOKEN_KEY = 'hashely_admin_token';

const emptyNewUser = {
  email: '',
  username: '',
  realname: '',
  completed: true,
  subscriptionStatus: 'FREE',
};

const MENUS = [
  { key: 'overview', label: 'Dashboard', icon: <DashboardRoundedIcon fontSize="small" /> },
  { key: 'members', label: 'Members', icon: <GroupRoundedIcon fontSize="small" /> },
  { key: 'subscriptions', label: 'Subscriptions', icon: <PaymentRoundedIcon fontSize="small" /> },
  { key: 'boards', label: 'Boards', icon: <DashboardCustomizeRoundedIcon fontSize="small" /> },
  { key: 'moderation', label: 'Moderation', icon: <ReportRoundedIcon fontSize="small" /> },
  { key: 'settings', label: 'Settings', icon: <SettingsRoundedIcon fontSize="small" /> },
];

const PLAN_OPTIONS = ['FREE', 'HOBBY', 'PRO', 'ULTRA'];

function formatTokens(n) {
  const value = Number(n) || 0;
  if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  return String(Math.round(value));
}

function formatUsd(n) {
  const value = Number(n) || 0;
  if (value > 0 && value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
}

function planChipColor(plan) {
  if (plan === 'ULTRA') return 'warning';
  if (plan === 'PRO') return 'secondary';
  if (plan === 'HOBBY' || plan === 'LIGHT') return 'primary';
  return 'default';
}

const StatCard = ({ label, value, hint }) => (
  <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #dbe6ff', flex: 1, minWidth: 140 }}>
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

const Admin = () => {
  const [token, setToken] = React.useState(sessionStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [selectedMenu, setSelectedMenu] = React.useState('overview');
  const [users, setUsers] = React.useState([]);
  const [dashboardUsers, setDashboardUsers] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [subscriptionUsers, setSubscriptionUsers] = React.useState([]);
  const [boards, setBoards] = React.useState([]);
  const [reports, setReports] = React.useState([]);
  const [memberQuery, setMemberQuery] = React.useState('');
  const [memberPlan, setMemberPlan] = React.useState('');
  const [boardQuery, setBoardQuery] = React.useState('');
  const [expandedUsers, setExpandedUsers] = React.useState({});
  const [drafts, setDrafts] = React.useState({});
  const [newUser, setNewUser] = React.useState(emptyNewUser);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (window.location.pathname !== ADMIN_PATH) return undefined;

    const tag = document.createElement('meta');
    tag.setAttribute('name', 'robots');
    tag.setAttribute('content', 'noindex, nofollow, noarchive');
    document.head.appendChild(tag);

    return () => {
      if (tag.parentNode) tag.parentNode.removeChild(tag);
    };
  }, []);

  const withAuth = React.useCallback((method, path, body) => {
    const options = {
      credentials: 'include',
      method,
      headers: {
        'Content-Type': 'application/json',
        'x-admin-token': token,
      },
    };
    if (body) options.body = JSON.stringify(body);
    return fetch(`${SERVER_ROOT}${path}`, options).then(async response => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.type || 'REQUEST_FAILED');
      return data;
    });
  }, [token]);

  const loadUsers = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (memberQuery.trim()) params.set('q', memberQuery.trim());
      if (memberPlan) params.set('plan', memberPlan);
      const qs = params.toString();
      const data = await withAuth('GET', `/auth/admin/users${qs ? `?${qs}` : ''}`);
      setUsers(data.users || []);
      const nextDrafts = {};
      (data.users || []).forEach(user => {
        nextDrafts[user._id] = {
          email: user.email || '',
          username: user.username || '',
          realname: user.realname || '',
          completed: !!user.completed,
          subscriptionStatus: user.subscriptionStatus || 'FREE',
        };
      });
      setDrafts(nextDrafts);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [withAuth, memberQuery, memberPlan]);

  const loadDashboard = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await withAuth('GET', '/auth/admin/dashboard');
      setSummary(data.summary || null);
      setDashboardUsers(data.users || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [withAuth]);

  const loadSubscriptions = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await withAuth('GET', '/auth/admin/subscriptions');
      setSubscriptionUsers(data.users || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [withAuth]);

  const loadBoards = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (boardQuery.trim()) params.set('q', boardQuery.trim());
      const qs = params.toString();
      const data = await withAuth('GET', `/auth/admin/boards${qs ? `?${qs}` : ''}`);
      setBoards(data.boards || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [withAuth, boardQuery]);

  const loadReports = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await withAuth('GET', '/auth/admin/reports');
      setReports(data.reports || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [withAuth]);

  const refresh = React.useCallback(async () => {
    if (selectedMenu === 'overview') await loadDashboard();
    else if (selectedMenu === 'members') await loadUsers();
    else if (selectedMenu === 'subscriptions') await loadSubscriptions();
    else if (selectedMenu === 'boards') await loadBoards();
    else if (selectedMenu === 'moderation') await loadReports();
  }, [selectedMenu, loadDashboard, loadUsers, loadSubscriptions, loadBoards, loadReports]);

  React.useEffect(() => {
    if (!token) return;
    if (selectedMenu === 'overview') loadDashboard();
    else if (selectedMenu === 'members') loadUsers();
    else if (selectedMenu === 'subscriptions') loadSubscriptions();
    else if (selectedMenu === 'boards') loadBoards();
    else if (selectedMenu === 'moderation') loadReports();
  }, [token, selectedMenu, loadDashboard, loadUsers, loadSubscriptions, loadBoards, loadReports]);

  const login = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${SERVER_ROOT}/auth/admin/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.type || 'LOGIN_FAILED');
      sessionStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken('');
    setUsers([]);
    setDashboardUsers([]);
    setSubscriptionUsers([]);
    setBoards([]);
    setReports([]);
    setSummary(null);
    setDrafts({});
  };

  const createUser = async () => {
    setLoading(true);
    setError('');
    try {
      await withAuth('POST', '/auth/admin/users', newUser);
      setNewUser(emptyNewUser);
      await loadUsers();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const saveUser = async userId => {
    setLoading(true);
    setError('');
    try {
      await withAuth('PUT', `/auth/admin/users/${userId}`, drafts[userId]);
      await loadUsers();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const resetUserAi = async userId => {
    setLoading(true);
    setError('');
    try {
      await withAuth('POST', `/auth/admin/users/${userId}/reset-ai`);
      await loadUsers();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async userId => {
    const target = users.find(u => u._id === userId);
    if (!target) return;
    if (!window.confirm(`Delete ${target.username || target.email || userId}?`)) return;

    setLoading(true);
    setError('');
    try {
      await withAuth('DELETE', `/auth/admin/users/${userId}`);
      await loadUsers();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteBoard = async board => {
    if (!window.confirm(`Delete board #${board.hashtag} (owner: ${board.username || board.userId})?`)) return;
    setLoading(true);
    setError('');
    try {
      await withAuth('DELETE', `/auth/admin/boards/${board._id}`);
      await loadBoards();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const resetBoardAi = async boardId => {
    setLoading(true);
    setError('');
    try {
      await withAuth('POST', `/auth/admin/boards/${boardId}/reset-ai`);
      await loadBoards();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const clearReport = async report => {
    setLoading(true);
    setError('');
    try {
      await withAuth('POST', `/auth/admin/reports/${report.type}/${report._id}/clear`);
      await loadReports();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteReportTarget = async report => {
    const label = report.type === 'content' ? 'take down this content' : `delete this ${report.type}`;
    if (!window.confirm(`Really ${label}?`)) return;
    setLoading(true);
    setError('');
    try {
      await withAuth('DELETE', `/auth/admin/reports/${report.type}/${report._id}`);
      await loadReports();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const updateDraft = (userId, key, value) => {
    setDrafts(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [key]: value,
      },
    }));
  };

  const toggleUser = userId => {
    setExpandedUsers(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  if (!token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(145deg, #f4f7ff 0%, #eef3ff 42%, #f9fbff 100%)',
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #dbe6ff' }}>
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Admin Login
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Sign in to manage users, billing, boards, and moderation.
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  label="Username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  autoComplete="off"
                  onKeyDown={e => {
                    if (e.key === 'Enter') login();
                  }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="new-password"
                  onKeyDown={e => {
                    if (e.key === 'Enter') login();
                  }}
                />
                {error ? <Alert severity="error">{error}</Alert> : null}
                <Button
                  variant="contained"
                  onClick={login}
                  disabled={loading}
                  sx={{ height: 44, fontWeight: 700, borderRadius: 2 }}
                >
                  Login
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(145deg, #f4f7ff 0%, #eef3ff 42%, #f9fbff 100%)',
        py: 2,
      }}
    >
      <Container maxWidth={false} sx={{ px: { xs: 1.5, md: 2.5 } }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Card
            elevation={0}
            sx={{
              width: 240,
              borderRadius: 4,
              border: '1px solid #dbe6ff',
              height: 'calc(100vh - 16px)',
              position: 'sticky',
              top: 8,
              display: { xs: 'none', md: 'block' },
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  HASHELY ADMIN
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                  Admin console
                </Typography>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <List disablePadding>
                {MENUS.map(menu => (
                  <ListItemButton
                    key={menu.key}
                    selected={selectedMenu === menu.key}
                    onClick={() => setSelectedMenu(menu.key)}
                    sx={{ borderRadius: 2, mb: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 34 }}>{menu.icon}</ListItemIcon>
                    <ListItemText primary={menu.label} />
                  </ListItemButton>
                ))}
              </List>
              <Box sx={{ mt: 2 }}>
                <Button
                  startIcon={<LogoutRoundedIcon />}
                  color="error"
                  variant="outlined"
                  onClick={logout}
                  fullWidth
                >
                  Logout
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #dbe6ff', mb: 2 }}>
              {loading ? <LinearProgress /> : null}
              <CardContent sx={{ p: 3 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {MENUS.find(m => m.key === selectedMenu)?.label || 'Admin'}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                      {selectedMenu === 'overview' && summary ? (
                        <React.Fragment>
                          <Chip label={`${summary.userCount} users`} size="small" color="primary" variant="outlined" />
                          <Chip label={`${summary.boardCount} boards`} size="small" variant="outlined" />
                          <Chip label={`${summary.paidCount || 0} paid`} size="small" color="success" variant="outlined" />
                          <Chip label={`${summary.reportCount || 0} reports`} size="small" color="warning" variant="outlined" />
                          <Chip label={formatUsd(summary.totalSpendUsd)} size="small" variant="outlined" />
                        </React.Fragment>
                      ) : null}
                      {selectedMenu === 'members' ? (
                        <Chip label={`${users.length} members`} size="small" color="primary" variant="outlined" />
                      ) : null}
                      {selectedMenu === 'subscriptions' ? (
                        <Chip label={`${subscriptionUsers.length} paid`} size="small" color="primary" variant="outlined" />
                      ) : null}
                      {selectedMenu === 'boards' ? (
                        <Chip label={`${boards.length} boards`} size="small" color="primary" variant="outlined" />
                      ) : null}
                      {selectedMenu === 'moderation' ? (
                        <Chip label={`${reports.length} open`} size="small" color="warning" variant="outlined" />
                      ) : null}
                      <Chip label="Protected Area" size="small" />
                    </Stack>
                  </Box>
                  <Button startIcon={<RefreshRoundedIcon />} variant="outlined" onClick={refresh} disabled={loading || selectedMenu === 'settings'}>
                    Refresh
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            {selectedMenu === 'overview' ? (
              <React.Fragment>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  <StatCard label="Users" value={summary?.userCount ?? '—'} />
                  <StatCard label="Boards" value={summary?.boardCount ?? '—'} />
                  <StatCard
                    label="Total tokens"
                    value={formatTokens(summary?.totalTokens)}
                    hint={`${summary?.totalAiRequests || 0} AI requests`}
                  />
                  <StatCard
                    label="Budget spend"
                    value={formatUsd(summary?.totalSpendUsd)}
                    hint="Gemini 3.1 Flash-Lite estimate"
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  <StatCard label="FREE" value={summary?.planCounts?.FREE ?? '—'} />
                  <StatCard label="HOBBY" value={summary?.planCounts?.HOBBY ?? '—'} />
                  <StatCard label="PRO" value={summary?.planCounts?.PRO ?? '—'} />
                  <StatCard label="ULTRA" value={summary?.planCounts?.ULTRA ?? '—'} />
                  <StatCard label="Incomplete" value={summary?.incompleteCount ?? '—'} hint="Onboarding not finished" />
                  <StatCard label="Open reports" value={summary?.reportCount ?? '—'} />
                </Stack>

                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #dbe6ff' }}>
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Users & boards
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Totals above are full database counts. Table shows the
                        {' '}
                        {summary?.listedUserLimit || 500}
                        {' '}
                        most recent users
                        {summary?.userCount > (summary?.listedUserLimit || 500)
                          ? ` (${summary.listedUserCount} of ${summary.userCount})`
                          : ''}
                        {' '}
                        — expand a row for board token usage.
                      </Typography>
                    </Box>
                    <Paper sx={{ overflowX: 'auto', boxShadow: 'none' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f8ff' }}>
                            <TableCell sx={{ width: 48 }} />
                            <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Boards</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Tokens</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Spend</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dashboardUsers.map(user => {
                            const open = !!expandedUsers[user._id];
                            return (
                              <React.Fragment key={user._id}>
                                <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleUser(user._id)}>
                                  <TableCell>
                                    <IconButton size="small">
                                      {open ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowRightRoundedIcon />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {user.username || '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {user.realname || ''}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{user.email || '—'}</TableCell>
                                  <TableCell align="right">{user.boardCount || 0}</TableCell>
                                  <TableCell align="right">{formatTokens(user.totalTokens)}</TableCell>
                                  <TableCell align="right">{formatUsd(user.estimatedCostUsd)}</TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={user.subscriptionStatus || 'FREE'}
                                      color={planChipColor(user.subscriptionStatus)}
                                      variant="outlined"
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                                    <Collapse in={open} timeout="auto" unmountOnExit>
                                      <Box sx={{ px: 2, py: 1.5, bgcolor: '#fafcff' }}>
                                        {(user.boards || []).length === 0 ? (
                                          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                            No boards yet.
                                          </Typography>
                                        ) : (
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>Board</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }} align="right">Pins</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }} align="right">Tokens</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }} align="right">Spend</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }} align="right">AI calls</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Last AI use</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Open</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {user.boards.map(board => (
                                                <TableRow key={board._id} hover>
                                                  <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                      #{board.hashtag}
                                                    </Typography>
                                                    {(board.currentStep || board.goal) ? (
                                                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 280, display: 'block' }}>
                                                        {board.currentStep || board.goal}
                                                      </Typography>
                                                    ) : null}
                                                  </TableCell>
                                                  <TableCell align="right">{board.pinCount}</TableCell>
                                                  <TableCell align="right">{formatTokens(board.totalTokens)}</TableCell>
                                                  <TableCell align="right">{formatUsd(board.estimatedCostUsd)}</TableCell>
                                                  <TableCell align="right">{board.requestCount || 0}</TableCell>
                                                  <TableCell>{formatDate(board.lastUsedAt)}</TableCell>
                                                  <TableCell>
                                                    <Button
                                                      size="small"
                                                      startIcon={<OpenInNewRoundedIcon />}
                                                      onClick={e => {
                                                        e.stopPropagation();
                                                        window.open(`/${user.username}/${encodeURIComponent(board.hashtag)}`, '_blank');
                                                      }}
                                                    >
                                                      Canvas
                                                    </Button>
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        )}
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })}
                          {!dashboardUsers.length && !loading ? (
                            <TableRow>
                              <TableCell colSpan={7}>
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                  No users found.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </TableBody>
                      </Table>
                    </Paper>
                  </CardContent>
                </Card>
              </React.Fragment>
            ) : null}

            {selectedMenu === 'members' ? (
              <React.Fragment>
                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #dbe6ff', mb: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} sx={{ mb: 2.5 }} alignItems={{ xs: 'stretch', md: 'center' }}>
                      <TextField
                        label="Search"
                        size="small"
                        placeholder="email, username, realname"
                        value={memberQuery}
                        onChange={e => setMemberQuery(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') loadUsers();
                        }}
                        sx={{ minWidth: 220 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Plan</InputLabel>
                        <Select
                          label="Plan"
                          value={memberPlan}
                          onChange={e => setMemberPlan(e.target.value)}
                        >
                          <MenuItem value="">All</MenuItem>
                          {PLAN_OPTIONS.map(plan => (
                            <MenuItem key={plan} value={plan}>{plan}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button variant="outlined" onClick={loadUsers} disabled={loading}>
                        Search
                      </Button>
                    </Stack>

                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Create Member
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', md: 'center' }}>
                      <TextField
                        label="Email"
                        size="small"
                        value={newUser.email}
                        onChange={e => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                      />
                      <TextField
                        label="Username"
                        size="small"
                        value={newUser.username}
                        onChange={e => setNewUser(prev => ({ ...prev, username: e.target.value }))}
                      />
                      <TextField
                        label="Realname"
                        size="small"
                        value={newUser.realname}
                        onChange={e => setNewUser(prev => ({ ...prev, realname: e.target.value }))}
                      />
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Plan</InputLabel>
                        <Select
                          label="Plan"
                          value={newUser.subscriptionStatus}
                          onChange={e => setNewUser(prev => ({ ...prev, subscriptionStatus: e.target.value }))}
                        >
                          {PLAN_OPTIONS.map(plan => (
                            <MenuItem key={plan} value={plan}>{plan}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!newUser.completed}
                            onChange={e => setNewUser(prev => ({ ...prev, completed: e.target.checked }))}
                          />
                        }
                        label="Completed"
                      />
                      <Button
                        startIcon={<AddRoundedIcon />}
                        variant="contained"
                        onClick={createUser}
                        disabled={loading}
                        sx={{ minWidth: 120, borderRadius: 2 }}
                      >
                        Create
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>

                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #dbe6ff' }}>
                  <CardContent sx={{ p: 0 }}>
                    <Paper sx={{ overflowX: 'auto', boxShadow: 'none' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f8ff' }}>
                            <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Username</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Realname</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Tokens used</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Completed</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {users.map(user => (
                            <TableRow key={user._id} hover>
                              <TableCell>
                                <TextField
                                  value={drafts[user._id]?.email || ''}
                                  onChange={e => updateDraft(user._id, 'email', e.target.value)}
                                  size="small"
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  value={drafts[user._id]?.username || ''}
                                  onChange={e => updateDraft(user._id, 'username', e.target.value)}
                                  size="small"
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  value={drafts[user._id]?.realname || ''}
                                  onChange={e => updateDraft(user._id, 'realname', e.target.value)}
                                  size="small"
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>
                                <Select
                                  size="small"
                                  value={drafts[user._id]?.subscriptionStatus || 'FREE'}
                                  onChange={e => updateDraft(user._id, 'subscriptionStatus', e.target.value)}
                                >
                                  {PLAN_OPTIONS.map(plan => (
                                    <MenuItem key={plan} value={plan}>{plan}</MenuItem>
                                  ))}
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {user.aiActionsUsed || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {user.aiActionsPeriodKey || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Checkbox
                                  checked={!!drafts[user._id]?.completed}
                                  onChange={e => updateDraft(user._id, 'completed', e.target.checked)}
                                />
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<SaveRoundedIcon />}
                                    onClick={() => saveUser(user._id)}
                                    disabled={loading}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<RestartAltRoundedIcon />}
                                    onClick={() => resetUserAi(user._id)}
                                    disabled={loading}
                                  >
                                    Reset tokens
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<OpenInNewRoundedIcon />}
                                    onClick={() => window.open(`/${drafts[user._id]?.username || user.username}`, '_blank')}
                                  >
                                    View
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    startIcon={<DeleteOutlineRoundedIcon />}
                                    onClick={() => deleteUser(user._id)}
                                    disabled={loading}
                                  >
                                    Delete
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                          {!users.length && !loading ? (
                            <TableRow>
                              <TableCell colSpan={7}>
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                  No members found.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </TableBody>
                      </Table>
                    </Paper>
                    <Divider />
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Changes are saved per row when you click Save. User delete does not cascade boards/comments.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </React.Fragment>
            ) : null}

            {selectedMenu === 'subscriptions' ? (
              <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #dbe6ff' }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Paid users
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      HOBBY, PRO, and ULTRA members with Paddle IDs and monthly AI usage.
                    </Typography>
                  </Box>
                  <Paper sx={{ overflowX: 'auto', boxShadow: 'none' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f8ff' }}>
                          <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Paddle customer</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Subscription ID</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Tokens used</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Open</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subscriptionUsers.map(user => (
                          <TableRow key={user._id} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.username || '—'}</Typography>
                              <Typography variant="caption" color="text.secondary">{user.realname || ''}</Typography>
                            </TableCell>
                            <TableCell>{user.email || '—'}</TableCell>
                            <TableCell>
                              <Chip size="small" label={user.subscriptionStatus} color={planChipColor(user.subscriptionStatus)} variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {user.paddleCustomerId || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {user.subscriptionId || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{user.aiActionsUsed || 0}</TableCell>
                            <TableCell>{user.aiActionsPeriodKey || '—'}</TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                startIcon={<OpenInNewRoundedIcon />}
                                onClick={() => window.open(`/${user.username}`, '_blank')}
                              >
                                Profile
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {!subscriptionUsers.length && !loading ? (
                          <TableRow>
                            <TableCell colSpan={8}>
                              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                No paid users yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  </Paper>
                </CardContent>
              </Card>
            ) : null}

            {selectedMenu === 'boards' ? (
              <React.Fragment>
                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #dbe6ff', mb: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', md: 'center' }}>
                      <TextField
                        label="Search"
                        size="small"
                        placeholder="hashtag, goal, or username"
                        value={boardQuery}
                        onChange={e => setBoardQuery(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') loadBoards();
                        }}
                        sx={{ minWidth: 260 }}
                      />
                      <Button variant="outlined" onClick={loadBoards} disabled={loading}>
                        Search
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>

                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #dbe6ff' }}>
                  <CardContent sx={{ p: 0 }}>
                    <Paper sx={{ overflowX: 'auto', boxShadow: 'none' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f8ff' }}>
                            <TableCell sx={{ fontWeight: 700 }}>Board</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Owner</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Pins</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Tokens</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Spend</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Last AI</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {boards.map(board => (
                            <TableRow key={board._id} hover>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>#{board.hashtag}</Typography>
                                {board.currentStep || board.goal ? (
                                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 240, display: 'block' }}>
                                    {board.currentStep || board.goal}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell>{board.username || '—'}</TableCell>
                              <TableCell>
                                <Chip size="small" label={board.subscriptionStatus || 'FREE'} color={planChipColor(board.subscriptionStatus)} variant="outlined" />
                              </TableCell>
                              <TableCell align="right">{board.pinCount || 0}</TableCell>
                              <TableCell align="right">{formatTokens(board.totalTokens)}</TableCell>
                              <TableCell align="right">{formatUsd(board.estimatedCostUsd)}</TableCell>
                              <TableCell>{formatDate(board.lastUsedAt)}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  <Button
                                    size="small"
                                    startIcon={<OpenInNewRoundedIcon />}
                                    onClick={() => window.open(`/${board.username}/${encodeURIComponent(board.hashtag)}`, '_blank')}
                                    disabled={!board.username}
                                  >
                                    Canvas
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<RestartAltRoundedIcon />}
                                    onClick={() => resetBoardAi(board._id)}
                                    disabled={loading}
                                  >
                                    Reset tokens
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    startIcon={<DeleteOutlineRoundedIcon />}
                                    onClick={() => deleteBoard(board)}
                                    disabled={loading}
                                  >
                                    Delete
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                          {!boards.length && !loading ? (
                            <TableRow>
                              <TableCell colSpan={8}>
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                  No boards found.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </TableBody>
                      </Table>
                    </Paper>
                  </CardContent>
                </Card>
              </React.Fragment>
            ) : null}

            {selectedMenu === 'moderation' ? (
              <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #dbe6ff' }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Report queue
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Content, comments, and replies with user reports. Clear dismisses reports; Delete removes the item (content is stubbed).
                    </Typography>
                  </Box>
                  <Paper sx={{ overflowX: 'auto', boxShadow: 'none' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f8ff' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Snippet</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Reports</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Updated</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reports.map(report => (
                          <TableRow key={`${report.type}-${report._id}`} hover>
                            <TableCell>
                              <Chip size="small" label={report.type} variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 420 }}>
                                {report.snippet}
                              </Typography>
                              {report.url ? (
                                <Typography
                                  variant="caption"
                                  color="primary"
                                  component="a"
                                  href={report.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  sx={{ display: 'block' }}
                                >
                                  {report.url}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell align="right">{report.reportCount || 0}</TableCell>
                            <TableCell>{formatDate(report.updatedAt || report.createdAt)}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => clearReport(report)}
                                  disabled={loading}
                                >
                                  Clear
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  startIcon={<DeleteOutlineRoundedIcon />}
                                  onClick={() => deleteReportTarget(report)}
                                  disabled={loading}
                                >
                                  {report.type === 'content' ? 'Take down' : 'Delete'}
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                        {!reports.length && !loading ? (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                No open reports.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  </Paper>
                </CardContent>
              </Card>
            ) : null}

            {selectedMenu === 'settings' ? (
              <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #dbe6ff' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Spend estimates use Gemini 3.1 Flash-Lite rates ($0.25 / 1M input, $1.50 / 1M output).
                    Usage starts accumulating on new board AI calls after deploy.
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Admin credentials (server env)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" component="div">
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      <li><code>ADMIN_USERNAME</code> — login username</li>
                      <li><code>ADMIN_PASSWORD</code> — login password (required in production; never leave default)</li>
                      <li><code>ADMIN_TOKEN</code> — token returned after login and sent as <code>x-admin-token</code></li>
                      <li><code>SESSION_SECRET</code> — Express session signing secret</li>
                      <li><code>MONGO_URL</code> — required MongoDB connection string</li>
                      <li><code>REDIS_URL</code> — Memorystore URL for multi-instance Socket.IO</li>
                    </Box>
                  </Typography>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Member delete removes only the User document — boards, comments, and GCS assets may be left behind.
                    Prefer plan downgrade + board delete from this console when cleaning up accounts.
                  </Alert>
                </CardContent>
              </Card>
            ) : null}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Admin;
